import React from "react";
import { Box, Chip, Typography } from "@mui/material";
import Seo from "../components/Seo";

import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import CookieOutlinedIcon from "@mui/icons-material/CookieOutlined";
import StorageOutlinedIcon from "@mui/icons-material/StorageOutlined";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";

import "./PrivacyPage.css";

const sections = [
    {
        icon: <StorageOutlinedIcon color="primary" />,
        title: "Какие данные обрабатываются",
        paragraphs: [
            "В рамках работы сервиса пользователь может загружать PDF-файлы платежных поручений, Excel-файлы учета, кадровые файлы и иные документы, содержащие персональные данные.",
            "К таким данным могут относиться ИИН, ФИО, банковские счета, суммы выплат, наименования организаций, БИН организаций и коды государственных учреждений.",
        ],
    },
    {
        icon: <ShieldOutlinedIcon color="primary" />,
        title: "Основной принцип обработки данных",
        paragraphs: [
            "Сервис не предназначен для хранения персональных данных, содержащихся в загружаемых документах.",
            "Все загруженные пользователем документы и содержащиеся в них персональные данные используются исключительно для предварительного анализа документов, сверки платежных поручений с данными учета, сверки платежных поручений с кадровыми данными и конвертации платежных поручений в структурированный реестр.",
            "Обработка выполняется автоматически в рамках конкретного пользовательского запроса и только для отображения результата самому пользователю.",
            "После завершения обработки персональные данные не сохраняются в базе данных сайта, не используются повторно и не передаются третьим лицам.",
        ],
        accent: true,
    },
    {
        icon: <LockOutlinedIcon color="primary" />,
        title: "Какие данные сайт не хранит",
        list: [
            "Содержимое загруженных PDF-файлов",
            "Содержимое Excel-файлов",
            "ИИН физических лиц",
            "ФИО сотрудников",
            "Банковские счета",
            "Суммы выплат",
            "Результаты сверки по конкретным сотрудникам",
            "Результаты конвертации по конкретным сотрудникам",
            "Персональные данные, содержащиеся в кадровых или учетных файлах",
        ],
    },
    {
        icon: <VerifiedUserOutlinedIcon color="primary" />,
        title: "Какие данные сайт может хранить",
        paragraphs: [
            "Для обеспечения работы сервиса сайт может хранить только общую техническую и обезличенную информацию, не позволяющую идентифицировать конкретное физическое лицо.",
            "К такой информации могут относиться сведения о банках, количестве файлов, длительности обработки, технических ошибках, а также данные, необходимые для проверки подписки пользователя.",
            "Такие сведения используются исключительно для обеспечения стабильной работы сервиса, диагностики технических проблем, ведения общей статистики и контроля доступа к функционалу.",
        ],
    },
    {
        icon: <DescriptionOutlinedIcon color="primary" />,
        title: "Обработка файлов",
        paragraphs: [
            "Загруженные пользователем файлы используются только в течение времени, необходимого для выполнения выбранной операции.",
            "После завершения обработки файлы не используются повторно и не предназначены для долгосрочного хранения.",
            "Сервис принимает меры для ограничения доступа к загружаемым документам и обеспечивает доступ к ним только авторизованному пользователю в рамках текущей сессии.",
        ],
    },
    {
        icon: <ShieldOutlinedIcon color="primary" />,
        title: "Защита данных",
        list: [
            "Использование защищенного соединения HTTPS",
            "Авторизация пользователей",
            "Разграничение доступа по подписке",
            "Ограничение доступа к файлам",
            "Защита пользовательских сессий",
            "Защита от несанкционированного доступа",
            "Ведение журналов технических ошибок и действий пользователей",
        ],
    },
    {
        icon: <CookieOutlinedIcon color="primary" />,
        title: "Cookies и технические данные",
        paragraphs: [
            "Сайт может использовать cookies, токены авторизации и иные технические средства для поддержания пользовательской сессии, авторизации, защиты от несанкционированного доступа, сохранения настроек интерфейса и анализа стабильности работы сайта.",
            "Cookies не используются для сбора персональных данных.",
        ],
    },
    {
        icon: <DescriptionOutlinedIcon color="primary" />,
        title: "Изменение политики",
        paragraphs: [
            "Администрация сайта вправе вносить изменения в настоящую Политику конфиденциальности.",
            "Актуальная редакция Политики всегда публикуется на сайте.",
        ],
    },
];

function PrivacyCard({ icon, title, paragraphs, list, accent = false }) {
    return (
        <div className={`privacy-card ${accent ? "privacy-card--accent" : ""}`}>
            <div className="privacy-card__header">
                <div className="privacy-card__icon">{icon}</div>
                <Typography className="privacy-card__title">
                    {title}
                </Typography>
            </div>

            {paragraphs && paragraphs.length > 0 && (
                <div className="privacy-card__content">
                    {paragraphs.map((text, index) => (
                        <Typography key={index} className="privacy-card__text">
                            {text}
                        </Typography>
                    ))}
                </div>
            )}

            {list && list.length > 0 && (
                <div className="privacy-card__content">
                    {list.map((item) => (
                        <div key={item} className="privacy-list-item">
                            <div className="privacy-list-item__dot" />
                            <Typography className="privacy-card__text privacy-card__text--list">
                                {item}
                            </Typography>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

export default function PrivacyPage() {
    return (
        <>
            <Seo
                title="Политика конфиденциальности – Qarzhy"
                description="Политика конфиденциальности сервиса Qarzhy. Условия обработки документов, ограничения хранения персональных данных, защита информации, cookies и технические меры безопасности."
                keywords="политика конфиденциальности Qarzhy, обработка персональных данных, защита данных, безопасность документов, cookies, PDF и Excel документы"
                url="https://qarzhy.kz/privacy"
            />

            <Box className="privacy-page">
                <div className="privacy-shell">
                    <section className="privacy-hero">
                        <Chip
                            label="Конфиденциальность и защита данных"
                            color="primary"
                            className="privacy-hero__chip"
                        />

                        <Typography variant="h1" className="privacy-hero__title">
                            Политика конфиденциальности
                        </Typography>

                        <Typography className="privacy-hero__subtitle">
                            Qarzhy ориентирован на обработку документов без избыточного хранения
                            персональных данных. Все результаты отображаются только пользователю,
                            а сайт хранит только ограниченную техническую и обезличенную информацию.
                        </Typography>
                    </section>

                    <div className="privacy-cards">
                        <PrivacyCard
                            icon={<ShieldOutlinedIcon color="primary" />}
                            title="Общие положения"
                            paragraphs={[
                                "Настоящая Политика конфиденциальности регулирует порядок обработки информации, получаемой при использовании веб-приложения для автоматизированной обработки банковских платежных поручений, сверки с данными учета, кадровыми данными и конвертации банковских документов.",
                                "Используя сайт, пользователь подтверждает согласие с условиями настоящей Политики конфиденциальности.",
                            ]}
                        />

                        <PrivacyCard
                            icon={<ShieldOutlinedIcon color="primary" />}
                            title="Главное правило"
                            paragraphs={[
                                "Qarzhy не сохраняет персональные данные сотрудников, содержимое PDF- и Excel-файлов, результаты сверки по конкретным сотрудникам и результаты конвертации. Обработка выполняется только для показа результата самому пользователю.",
                            ]}
                            accent
                        />

                        {sections.map((section) => (
                            <PrivacyCard
                                key={section.title}
                                icon={section.icon}
                                title={section.title}
                                paragraphs={section.paragraphs}
                                list={section.list}
                                accent={section.accent}
                            />
                        ))}
                    </div>
                </div>
            </Box>
        </>
    );
}